import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { Api } from './api.service';
import { AuthService } from './auth.service';

export type MailluneLocale = 'en' | 'sv' | 'no' | 'da' | 'fr' | 'es';

const SUPPORTED_LOCALES: MailluneLocale[] = ['en', 'sv', 'no', 'da', 'fr', 'es'];

export interface MailluneMergeTag {
    name: string;
    value: string;
    sample?: string;
}

export interface FlowPlaceholder {
    placeHolder: string;
    label: string;
}

// Maillune's own REST API. The SDK's bundled web component calls this directly from
// the browser using the same short-lived session token we already mint server-side —
// this mirrors that, for the one action (save-as-template) the SDK doesn't expose publicly.
const MAILLUNE_API_URL = 'https://api.maillune.com/v1';

@Injectable({ providedIn: 'root' })
export class MailluneService {
    private api = inject(Api);
    private auth = inject(AuthService);
    private translate = inject(TranslateService);
    private http = inject(HttpClient);

    // Full Beefree replacement, toggled per organization by superadmin.
    get enabled(): boolean {
        return !!this.auth.organization()?.mailluneEditorEnabled;
    }

    async getSessionToken(): Promise<string | null> {
        try {
            const result = await this.api.crm().get<{ token: string; expiresIn: number }>('/third-party/maillune/token');
            return result.success && result.data ? result.data.token : null;
        } catch (e) {
            console.error('Failed to get Maillune session token:', e);
            return null;
        }
    }

    // Persists the current design into Maillune's own reusable template library
    // (distinct from the CRM's own "Save", which stores the design in our DB).
    // Replicates the SDK's private templateService.create() call directly, using the
    // same session token already used as the <email-builder> api-key attribute.
    async saveAsTemplate(token: string, userId: string, name: string, blockTree: any): Promise<void> {
        await this.http
            .post(`${MAILLUNE_API_URL}/templates`, { name, userId, blockTree }, { headers: { Authorization: `Bearer ${token}` } })
            .toPromise();
    }

    get locale(): MailluneLocale {
        const settings = this.auth.currentUser()?.settings;
        const language = (settings?.language || navigator.language || 'en').slice(0, 2).toLowerCase();
        return SUPPORTED_LOCALES.includes(language as MailluneLocale) ? (language as MailluneLocale) : 'en';
    }

    // org-app toggles dark mode via [data-theme="dark"] on the root element (fb.theme.ts),
    // not crm-app's .dark-mode class.
    get theme(): 'light' | 'dark' {
        return document.documentElement.getAttribute('data-theme') === 'dark' ? 'dark' : 'light';
    }

    // org-app's brand color is a live per-venue CSS custom property, not a fixed constant.
    get accentColor(): string {
        return getComputedStyle(document.documentElement).getPropertyValue('--venue-brand').trim() || '#f13a28';
    }

    buildMergeTags(placeholders: FlowPlaceholder[]): MailluneMergeTag[] {
        return placeholders.map((p) => {
            const label = this.translate.instant(p.label);
            return { name: label, value: p.placeHolder, sample: label };
        });
    }
}
